import type { ILanguageResources, ITranslationModule } from '../../../interfaces';
import { interpolate } from '../../../global';

class Id {
  private constructor() {}
  public static readonly prefix = 'UI.scripts.franchisee.view.FranchiseeLaborRateMatrixView.';

  public static readonly laborRates = `${Id.prefix}laborRates`;
  public static readonly loading = `${Id.prefix}loading`;
  public static readonly supplierOffline = `${Id.prefix}supplierOffline`;
  public static readonly unsupported = `${Id.prefix}unsupported`;
  public static readonly loadFailed = `${Id.prefix}loadFailed`;
  public static readonly noLibraries = `${Id.prefix}noLibraries`;
  public static readonly library = `${Id.prefix}library`;
  public static readonly layer = `${Id.prefix}layer`;
  public static readonly setting = `${Id.prefix}setting`;
  public static readonly settingOption = `${Id.prefix}settingOption`;
  public static readonly cellLabel = `${Id.prefix}cellLabel`;
  public static readonly laborRatesSaved = `${Id.prefix}laborRatesSaved`;
  public static readonly ratesInCurrency = `${Id.prefix}ratesInCurrency`;
  public static readonly factorsLegend = `${Id.prefix}factorsLegend`;
}

export class LangFranchiseeLaborRateMatrixView {
  private constructor() {}

  public static laborRates(): string {
    return interpolate(Id.laborRates);
  }
  public static loading(): string {
    return interpolate(Id.loading);
  }
  public static supplierOffline(): string {
    return interpolate(Id.supplierOffline);
  }
  public static unsupported(): string {
    return interpolate(Id.unsupported);
  }
  public static loadFailed(): string {
    return interpolate(Id.loadFailed);
  }
  public static noLibraries(): string {
    return interpolate(Id.noLibraries);
  }
  public static library(): string {
    return interpolate(Id.library);
  }
  public static layer(): string {
    return interpolate(Id.layer);
  }
  public static setting(): string {
    return interpolate(Id.setting);
  }
  public static settingOption(half: 'rates' | 'factors'): string {
    return interpolate(Id.settingOption, null, half);
  }
  public static cellLabel(half: 'rates' | 'factors', area: string, type: string): string {
    return interpolate(Id.cellLabel, { area, type }, half);
  }
  public static laborRatesSaved(): string {
    return interpolate(Id.laborRatesSaved);
  }
  public static ratesInCurrency(currency: string): string {
    return interpolate(Id.ratesInCurrency, { currency });
  }
  public static factorsLegend(): string {
    return interpolate(Id.factorsLegend);
  }
}

export class FranchiseeLaborRateMatrixViewModule implements ITranslationModule {
  registerItems(register: ILanguageResources) {
    register.add(Id.laborRates, 'Labor Rates', 'Tab caption for the fabricator labor rate matrix editor');
    register.add(Id.loading, 'Loading the labor rate matrix...', 'Shown while the matrix is read from the supplier');
    register.add(
      Id.supplierOffline,
      'The supplier is offline, so the labor rate matrix cannot be loaded.',
      'Shown when no supplier system is online to read the matrix from'
    );
    register.add(
      Id.unsupported,
      "The supplier's frame configuration does not support labor rate overrides. It needs to be upgraded first.",
      'Shown when the supplier frame configuration is older than the version that can read the labor rate matrix'
    );
    register.add(
      Id.loadFailed,
      'The labor rate matrix could not be loaded.',
      'Shown after the matrix read failed; the error itself has already been displayed'
    );
    register.add(
      Id.noLibraries,
      'The supplier has no labor rate matrix to override.',
      'Shown when the supplier system returns no labor libraries'
    );
    register.add(Id.library, 'Library', 'Label for the labor library selector');
    register.add(Id.layer, 'Layer', 'Label for the labor layer selector');
    register.add(Id.setting, 'Setting', 'Label for the labor rate matrix Setting selector (rates vs factors)');
    register
      .add(Id.settingOption, 'Rates', 'Option text for the labor rate matrix Setting selector, by half')
      .addSegment('rates', 'Rates')
      .addSegment('factors', 'Factors');
    register
      .add(
        Id.cellLabel,
        'Rate for {{area}}, {{type}}',
        'Accessible name for one override cell of the labor rate grid, by half; area and type are the row and column codes'
      )
      .addSegment('rates', 'Rate for {{area}}, {{type}}')
      .addSegment('factors', 'Factor for {{area}}, {{type}}');
    register.add(Id.laborRatesSaved, 'Labor rates saved', 'Toast after the labor rate overrides are saved');
    register.add(
      Id.ratesInCurrency,
      'Rates in {{currency}} - grey cells use the default rate, white cells are overridden; clear a cell to remove its override',
      'Caption showing the currency the labor rate grid is priced in and explaining the grey/white cell legend, shown only on the rates half'
    );
    register.add(
      Id.factorsLegend,
      'Grey cells use the default factor, white cells are overridden; clear a cell to remove its override',
      'Caption explaining the grey/white cell legend, shown only on the factors half'
    );
  }
}
